import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    Typography,
    Chip,
    Stack,
    Paper,
    Tabs,
    Tab,
    CircularProgress,
    Divider,
} from '@mui/material';
import { Edit, AutoAwesome, Refresh, Description, CheckCircle, Delete, Check } from '@mui/icons-material';
import { PromptVersion } from './types';


interface PromptEditorProps {
    jobId: string;
    expertId: string;
    activePrompt?: PromptVersion;
    versions: PromptVersion[];
    selectedVersionId: string;
    onVersionSelect: (versionId: string) => void;
    onSetAsActive: (versionId: string) => void;
    onRegeneratePrompt: (experimentName: string, modificationRequest: string) => void;
    isRegenerating?: boolean;
    onNavigateToPreview?: () => void;
    draftPrompt: string;
    onDraftPromptChange: (prompt: string) => void;
    onSave: () => void;
}

// AI suggestion chips for prompt modifications
const AI_SUGGESTIONS = [
    'Increase technical skills weight',
    'Focus more on culture fit',
    'Prioritize recent experience',
    'Add leadership evaluation',
    'Be stricter on experience requirements',
    'Weight communication skills higher',
];

type SubTab = 'refine' | 'preview';

export const PromptEditor: React.FC<PromptEditorProps> = ({
    jobId,
    expertId,
    activePrompt,
    versions,
    selectedVersionId,
    onVersionSelect,
    onSetAsActive,
    onRegeneratePrompt,
    isRegenerating = false,
    onNavigateToPreview,
    draftPrompt,
    onDraftPromptChange,
    onSave,
}) => {
    const [subTab, setSubTab] = useState<SubTab>('refine');
    const [experimentName, setExperimentName] = useState('');
    const [modificationRequest, setModificationRequest] = useState('');

    const selectedVersion = versions.find(v => v.id === selectedVersionId);
    const displayPrompt = draftPrompt || selectedVersion?.prompt?.userTemplate || '';
    const systemPrompt = selectedVersion?.prompt?.system || '';

    // Populate fields from draft when draft is selected
    useEffect(() => {
        if (selectedVersion?.status === 'DRAFT') {
            if (selectedVersion.experimentName) {
                setExperimentName(selectedVersion.experimentName);
            }
            if (selectedVersion.notes) {
                setModificationRequest(selectedVersion.notes);
            }
        } else {
            // Clear fields when switching to non-draft version
            setExperimentName('');
            setModificationRequest('');
        }
    }, [selectedVersionId, selectedVersion?.status]);

    const handleSuggestionClick = (suggestion: string) => {
        setModificationRequest(prev => {
            if (!prev.trim()) {
                return suggestion;
            }
            return `${prev}, ${suggestion}`;
        });
    };

    const handleRegeneratePrompt = () => {
        onRegeneratePrompt(experimentName, modificationRequest);
    };

    const handleSubTabChange = (event: React.SyntheticEvent, newValue: SubTab) => {
        setSubTab(newValue);
    };

    const handleSetAsActive = () => {
        onSetAsActive(selectedVersionId);
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Sub-tabs: Refine and Preview */}
            <Box sx={{ mb: 3 }}>
                <Tabs
                    value={subTab}
                    onChange={handleSubTabChange}
                    sx={{
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                        },
                    }}
                >
                    <Tab
                        icon={<AutoAwesome sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="Refine"
                        value="refine"
                    />
                    <Tab
                        icon={<Description sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                Preview
                                {selectedVersion?.status === 'DRAFT' && (
                                    <Chip
                                        label="1"
                                        size="small"
                                        sx={{
                                            height: 18,
                                            fontSize: '0.7rem',
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                        }}
                                    />
                                )}
                            </Box>
                        }
                        value="preview"
                    />
                </Tabs>
            </Box>

            {/* Refine Sub-tab Content */}
            {subTab === 'refine' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, overflow: 'auto', pr: 1 }}>
                    {/* Version Selector */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            bgcolor: '#f8f9fa',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Refresh sx={{ fontSize: 20, color: 'primary.main' }} />
                            <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                                Select Version or Experiment
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                            Choose a saved version or continue with an experiment.
                        </Typography>

                        <FormControl fullWidth size="small">
                            <Select
                                value={selectedVersionId}
                                onChange={(e) => {
                                    console.log('Select onChange triggered!');
                                    console.log('Selected value:', e.target.value);
                                    console.log('Current selectedVersionId:', selectedVersionId);
                                    onVersionSelect(e.target.value);
                                }}
                                displayEmpty
                                renderValue={(selected) => {
                                    const selectedVer = versions.find(v => v.id === selected);
                                    if (!selectedVer) return 'Select version';

                                    const formattedDate = new Date(selectedVer.createdAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                    });

                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {selectedVer.status === 'DRAFT' && (
                                                <Edit sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            )}
                                            {selectedVer.experimentName && (
                                                <Typography component="span" fontWeight={500}>
                                                    {selectedVer.experimentName}
                                                </Typography>
                                            )}
                                            <Typography component="span" fontWeight={500} color={selectedVer.experimentName ? 'text.secondary' : 'text.primary'}>
                                                v{selectedVer.version}
                                            </Typography>
                                            {selectedVer.isActive && (
                                                <Chip
                                                    label="Active"
                                                    color="primary"
                                                    size="small"
                                                    sx={{ height: 18, fontSize: '0.7rem' }}
                                                />
                                            )}
                                            <Typography component="span" variant="caption" color="text.secondary">
                                                — {formattedDate}
                                            </Typography>
                                        </Box>
                                    );
                                }}
                                sx={{
                                    bgcolor: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'divider',
                                    },
                                }}
                            >
                                {/* Saved Versions */}
                                <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                    Saved Versions
                                </MenuItem>
                                {versions
                                    .filter(v => v.status !== 'DRAFT')
                                    .map((version) => {
                                        const formattedDate = new Date(version.createdAt).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                        });

                                        const isSelected = version.id === selectedVersionId;

                                        return (
                                            <MenuItem key={version.id} value={version.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pointerEvents: 'none' }}>
                                                    {isSelected ? (
                                                        <Check sx={{ fontSize: 16, color: 'primary.main' }} />
                                                    ) : (
                                                        <Box sx={{ width: 16 }} />
                                                    )}
                                                    {version.experimentName && (
                                                        <Typography fontWeight={500}>{version.experimentName}</Typography>
                                                    )}
                                                    <Typography fontWeight={500} color={version.experimentName ? 'text.secondary' : 'text.primary'}>
                                                        v{version.version}
                                                    </Typography>
                                                    {version.isActive && (
                                                        <Chip
                                                            label="Active"
                                                            color="primary"
                                                            size="small"
                                                            sx={{ height: 18, fontSize: '0.7rem' }}
                                                        />
                                                    )}
                                                    <Typography variant="caption" color="text.secondary">
                                                        — {formattedDate}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}

                                {/* Your Experiments - Inside dropdown */}
                                {versions.filter(v => v.status === 'DRAFT').length > 0 && <Divider sx={{ my: 1 }} />}
                                {versions.filter(v => v.status === 'DRAFT').length > 0 && (
                                    <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                        Your Experiments
                                    </MenuItem>
                                )}
                                {versions
                                    .filter(v => v.status === 'DRAFT')
                                    .map((draft) => {
                                        const formattedDate = new Date(draft.createdAt).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                        });
                                        const isSelected = draft.id === selectedVersionId;

                                        return (
                                            <MenuItem
                                                key={draft.id}
                                                value={draft.id}
                                                onClick={(e) => {
                                                    // Check if click was on delete icon
                                                    const target = e.target as HTMLElement;
                                                    if (target.closest('[data-delete-icon]')) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        return; // Don't select if clicking delete
                                                    }
                                                    // Call onVersionSelect directly
                                                    console.log('Draft MenuItem clicked:', draft.id);
                                                    onVersionSelect(draft.id);
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                    {isSelected ? (
                                                        <Check sx={{ fontSize: 16, color: 'primary.main' }} />
                                                    ) : (
                                                        <Box sx={{ width: 16 }} />
                                                    )}
                                                    <Edit sx={{ fontSize: 16, color: 'primary.main' }} />
                                                    <Typography fontWeight={500}>
                                                        {draft.experimentName || 'New draft version'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        from v{draft.version - 1}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        • {formattedDate}
                                                    </Typography>
                                                    <Box
                                                        data-delete-icon
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            console.log('Delete draft:', draft.id);
                                                        }}
                                                        sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}
                                                    >
                                                        <Delete
                                                            sx={{
                                                                fontSize: 16,
                                                                color: 'error.main',
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    color: 'error.dark',
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                            </Select>
                        </FormControl>

                        {/* Set as Active Button - shown when non-active version is selected */}
                        {selectedVersion && !selectedVersion.isActive && selectedVersion.status !== 'DRAFT' && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CheckCircle />}
                                sx={{ mt: 2, textTransform: 'none', fontWeight: 500 }}
                                onClick={handleSetAsActive}
                            >
                                Set as Active
                            </Button>
                        )}
                    </Paper>

                    {/* Experiment Name */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            bgcolor: '#f8f9fa',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Edit sx={{ fontSize: 20, color: 'primary.main' }} />
                            <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                                Experiment Name
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                            {selectedVersion?.status === 'DRAFT'
                                ? 'Give your experiment a descriptive name to easily identify it later.'
                                : 'Name your experiment before generating. Leave blank for auto-naming.'
                            }
                        </Typography>

                        <TextField
                            fullWidth
                            size="small"
                            placeholder="e.g., Python Focus Test (optional)"
                            value={experimentName}
                            onChange={(e) => setExperimentName(e.target.value)}
                            sx={{
                                bgcolor: 'white',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'divider',
                                    },
                                },
                            }}
                        />
                    </Paper>

                    {/* Prompt Display Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            bgcolor: '#f8f9fa',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <AutoAwesome sx={{ fontSize: 20, color: 'primary.main' }} />
                            <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                                {selectedVersion?.status === 'DRAFT'
                                    ? 'Experiment Prompt'
                                    : selectedVersion?.isActive
                                        ? 'Current Active Prompt'
                                        : 'Selected Prompt'
                                }
                            </Typography>
                            {/* Show tags only for non-draft versions */}
                            {selectedVersion?.status !== 'DRAFT' && (
                                <>
                                    <Chip
                                        label={`v${selectedVersion?.version || 1}`}
                                        color="primary"
                                        size="small"
                                        sx={{ height: 18, fontSize: '0.7rem' }}
                                    />
                                    {selectedVersion?.isActive && (
                                        <Chip
                                            label="Active"
                                            color="success"
                                            size="small"
                                            sx={{ height: 18, fontSize: '0.7rem' }}
                                        />
                                    )}
                                </>
                            )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                            {selectedVersion?.status === 'DRAFT' ? (
                                // For drafts, show "Based on version X"
                                <>
                                    Based on version {selectedVersion.version}
                                    {selectedVersion?.notes && ` • ${selectedVersion.notes}`}
                                </>
                            ) : (
                                // For non-drafts, show "Created by"
                                <>
                                    Created by {selectedVersion?.createdBy || 'System'} on{' '}
                                    {selectedVersion ? new Date(selectedVersion.createdAt).toLocaleDateString() : 'N/A'}
                                    {selectedVersion?.notes && ` • ${selectedVersion.notes}`}
                                </>
                            )}
                        </Typography>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: 'white',
                                overflow: 'auto',
                                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                fontSize: '0.8rem',
                                lineHeight: 1.6,
                                whiteSpace: 'pre-wrap',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                minHeight: '200px',
                                maxHeight: '300px',
                                color: 'text.primary',
                            }}
                        >
                            {displayPrompt}
                        </Paper>
                    </Paper>

                    {/* AI Modification Request */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            bgcolor: '#f8f9fa',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <AutoAwesome sx={{ fontSize: 20, color: 'primary.main' }} />
                            <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                                What would you like to change?
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                            Describe your changes in plain language. AI will modify the prompt accordingly.
                        </Typography>

                        {/* Suggestion Chips */}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
                            {AI_SUGGESTIONS.map((suggestion) => (
                                <Chip
                                    key={suggestion}
                                    label={suggestion}
                                    size="small"
                                    onClick={() => handleSuggestionClick(suggestion)}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: 'white',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        '&:hover': {
                                            bgcolor: 'primary.light',
                                            borderColor: 'primary.main',
                                            color: 'primary.contrastText',
                                        },
                                    }}
                                />
                            ))}
                        </Stack>

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            placeholder="E.g., 'Weight Python and AWS skills more heavily' or 'Focus on leadership experience for senior roles'"
                            value={modificationRequest}
                            onChange={(e) => setModificationRequest(e.target.value)}
                            sx={{
                                bgcolor: 'white',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'divider',
                                    },
                                },
                            }}
                        />

                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                variant="contained"
                                startIcon={isRegenerating ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                                onClick={handleRegeneratePrompt}
                                disabled={!modificationRequest.trim() || isRegenerating}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3,
                                }}
                            >
                                {isRegenerating ? 'Refining...' : 'Regenerate Prompt'}
                            </Button>
                            {selectedVersion?.status === 'DRAFT' && (
                                <Button
                                    variant="outlined"
                                    onClick={() => setSubTab('preview')}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 500,
                                    }}
                                >
                                    Next →
                                </Button>
                            )}
                        </Box>
                    </Paper>
                </Box>
            )}

            {/* Preview Sub-tab Content */}
            {subTab === 'preview' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, flex: 1 }}>
                    {selectedVersion?.status === 'DRAFT' ? (
                        <>
                            <Box>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    Generated Prompt
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Review your prompt before testing.
                                </Typography>
                            </Box>

                            <Paper
                                elevation={0}
                                sx={{
                                    flex: 1,
                                    p: 3,
                                    bgcolor: '#f8f9fa',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    overflow: 'auto',
                                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    color: 'text.primary',
                                    minHeight: '400px',
                                    maxHeight: '400px',
                                }}
                            >
                                {displayPrompt || 'No prompt to preview. Please create or select a prompt in the Refine tab.'}
                            </Paper>

                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setSubTab('refine')}
                                    sx={{ textTransform: 'none', fontWeight: 500 }}
                                >
                                    Back to Refine
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<Refresh />}
                                    sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
                                >
                                    Test with Sample Candidates
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                p: 4,
                                bgcolor: '#f8f9fa',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}
                        >
                            <Description sx={{ fontSize: 64, color: 'text.disabled' }} />
                            <Typography variant="h6" color="text.secondary" textAlign="center">
                                No refined prompt yet.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                Use the Refine tab to modify the current prompt.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};
